import {
  createDefaultUser,
  type User,
} from "@-/dev.improve.haniverse.database.schemas.actor.schema";
import { getStorage } from "@-/dev.improve.haniverse.lib.storage";
import { useCallback, useMemo, useSyncExternalStore } from "react";
import {
  availableHanziScript,
  useTraditionalCharactersEnabled,
} from "@-/dev.improve.haniverse.lib.posthog";

// MMKV key holding the JSON-serialized singleton actor `user` row. Reads are
// synchronous (parse from MMKV); the DB remains the source of truth for async
// contexts (see `useActorUserDataQueryOptions`), while this cache is what
// render-time consumers read via `useActorUser()`. The cache is seeded by the
// actor-user-data query on resolve and kept fresh by `useActorUserCacheSync`
// (which re-reads the row on every committed `user` write).
const ACTOR_USER_CACHE_KEY = "actor.user";

// `useSyncExternalStore` requires `getSnapshot` to return a referentially-stable
// value when nothing changed. Parsing the JSON fresh on every read yields a new
// object each time, which would compare unequal forever and spin an infinite
// render loop, so memoize the parsed row keyed by the raw stored string: an
// unchanged read returns the same reference.
let snapshot: { raw: string; value: User } | null = null;

/**
 * Writes the singleton actor `user` row into the synchronous MMKV cache. Called
 * by the actor-user-data query (to hydrate the initial default snapshot) and
 * by `useActorUserCacheSync` (to refresh it after every committed `user` write —
 * local or remote-sync).
 */
export function setActorUserCache(user: User): void {
  getStorage().set(ACTOR_USER_CACHE_KEY, JSON.stringify(user));
}

/**
 * Clears the cached actor `user` row. Called on actor reset (alongside the DB
 * wipe) so the next read re-seeds from the freshly bootstrapped row.
 */
export function clearActorUserCache(): void {
  getStorage().remove(ACTOR_USER_CACHE_KEY);
  snapshot = null;
}

/**
 * Synchronously reads the cached actor `user` row.
 *
 * Non-hook: use this from async contexts that already run after the cache is
 * seeded but want the value without `await` (query/mutation functions that need,
 * e.g., the active `hanziScript`). For render-time reactivity use
 * {@link useActorUser}.
 *
 * Before the actor database query hydrates MMKV, returns a stable constructor-
 * default user so render-time reads never block startup. Async contexts that
 * need an authoritative identity or current DB state must continue to fetch
 * `useActorUserDataQueryOptions` rather than using this fallback.
 */
export function getActorUser(): User {
  const raw = getStorage().getString(ACTOR_USER_CACHE_KEY);
  if (raw === undefined) {
    return createDefaultUser;
  }
  if (snapshot !== null && snapshot.raw === raw) {
    return snapshot.value;
  }
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- the cache only ever holds a serialized User
  const value = JSON.parse(raw) as User;
  snapshot = { raw, value };
  return value;
}

/**
 * Reactive, synchronous read of the singleton actor `user` row (which carries
 * the per-user settings columns), falling back to the constructor defaults
 * until MMKV is hydrated. Re-renders the consumer whenever the cached row
 * changes — the initial actor DB load and later writes flow through here.
 *
 * Replaces reading the user off `AppLayoutContext`: it's the canonical sync read
 * path for settings like `hanziScript` / `hskVersion` / tone colors. The script
 * is restricted to the currently available feature set; the raw preference in
 * `getActorUser()` and the database is preserved when traditional access is off.
 */
export function useActorUser(): User {
  const subscribe = useCallback((onChange: () => void) => {
    const listener = getStorage().addOnValueChangedListener((changedKey) => {
      if (changedKey === ACTOR_USER_CACHE_KEY) {
        onChange();
      }
    });
    return () => {
      listener.remove();
    };
  }, []);
  const user = useSyncExternalStore(subscribe, getActorUser, getActorUser);
  const traditionalCharactersEnabled = useTraditionalCharactersEnabled();
  return useMemo(() => {
    const hanziScript = availableHanziScript(user.hanziScript, traditionalCharactersEnabled);
    return hanziScript === user.hanziScript ? user : { ...user, hanziScript };
  }, [traditionalCharactersEnabled, user]);
}
